import { useCallback, useEffect, useState } from "react";
import { PrizeDto } from "../models/Lottery";
import { useConfig } from "./useConfig";
import { useSnackbar } from "notistack";

const toFromData = (request: PrizeDto): FormData => {
  const formData = new FormData();
  formData.append("id", request.id ?? "");
  formData.append("name", request.name);
  formData.append("description", request.description ?? "");
  formData.append("referenceNumber", request.referenceNumber ?? "");
  formData.append("quantity", request.quantity.toString());
  if (request.imageData) {
    formData.append("imageData", request.imageData as any);
  }
  formData.append("lotterySessionId", request.lotterySessionId);
  formData.append("winningTicketNumber", "");
  formData.append("drawDateUtc", "");
  formData.append("drawRound", "");
  return formData;
};

export const usePrize = (id: string | undefined) => {
  const [prize, setPrize] = useState<PrizeDto | null>(null);
  const [loading, setLoading] = useState(false);
  const { config } = useConfig();
  const { enqueueSnackbar } = useSnackbar();

  const fetchPrize = useCallback(async () => {
    try {
      if (!id || id === "new") return;
      setLoading(true);
      const res = await fetch(`${config.apiUrl}/lottery/prize/${id}`, {
        method: "GET",
      });

      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }

      const json = (await res.json()) as PrizeDto;
      setPrize(json);
    } catch (err) {
      enqueueSnackbar(`${err}`, {
        variant: "error",
        autoHideDuration: 4000,
      });
    } finally {
      setLoading(false);
    }
  }, [config.apiUrl, enqueueSnackbar, id]);

  const addPrize = useCallback(
    async (request: PrizeDto) => {
      try {
        setLoading(true);
        const res = await fetch(`${config.apiUrl}/lottery/prize`, {
          method: "POST",
          body: toFromData(request),
        });

        if (!res.ok)
          throw new Error(`Request failed with status ${res.status}`);

        const json = (await res.json()) as PrizeDto;
        setPrize(json);
        enqueueSnackbar("Το δώρο προστέθηκε επιτυχώς!", {
          variant: "success",
          autoHideDuration: 4000,
        });
        return json;
      } catch (err) {
        enqueueSnackbar(`${err}`, {
          variant: "error",
          autoHideDuration: 4000,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [config.apiUrl, enqueueSnackbar]
  );

  const updatePrize = useCallback(
    async (request: PrizeDto) => {
      try {
        setLoading(true);
        const res = await fetch(`${config.apiUrl}/lottery/prize`, {
          method: "PUT",
          body: toFromData(request),
        });

        if (!res.ok)
          throw new Error(`Request failed with status ${res.status}`);

        const json = (await res.json()) as PrizeDto;
        enqueueSnackbar("Το δώρο ενημερώθηκε επιτυχώς!", {
          variant: "success",
          autoHideDuration: 4000,
        });
        setPrize(json);
        return json;
      } catch (err) {
        enqueueSnackbar(`${err}`, {
          variant: "error",
          autoHideDuration: 4000,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [config.apiUrl, enqueueSnackbar]
  );

  const deletePrize = useCallback(
    async (id: string) => {
      try {
        setLoading(true);

        const res = await fetch(`${config.apiUrl}/lottery/prize/${id}`, {
          method: "DELETE",
        });

        if (!res.ok)
          throw new Error(`Request failed with status ${res.status}`);

        setPrize(null);
      } catch (err) {
        enqueueSnackbar(`${err}`, {
          variant: "error",
          autoHideDuration: 4000,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [config.apiUrl, enqueueSnackbar]
  );

  useEffect(() => {
    fetchPrize();
  }, [fetchPrize]);

  return {
    prize,
    addPrize,
    updatePrize,
    deletePrize,
    fetchPrize,
    loading,
  };
};
